import { useEffect, useState } from "react";
import { ShoppingBag, Mail, Flame, ArrowLeft, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { fetchMerch, resolveImageUrl } from "@/lib/api";

const Merch = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchMerch().then(setData).catch(() => setData({ settings: {}, products: [] }));
  }, []);

  if (!data) {
    return (
      <PageLayout testId="merch-page">
        <div className="min-h-[60vh] flex items-center justify-center text-white/50 uppercase tracking-widest text-sm">Loading…</div>
      </PageLayout>
    );
  }

  const settings = data.settings || {};
  const products = data.products || [];
  const isLive = settings.status === "live" && products.length > 0;

  return (
    <PageLayout testId="merch-page">
      {isLive ? <ShopGrid settings={settings} products={products} /> : <ComingSoon settings={settings} />}
    </PageLayout>
  );
};

const ComingSoon = ({ settings }) => {
  const bg = resolveImageUrl(settings.background_image_url || "/assets/photos/IMG_3967.jpeg");
  const preview = settings.preview_items && settings.preview_items.length
    ? settings.preview_items
    : ["Squad Tee", "Hoodie", "Trucker Hat", "Sticker Pack"];

  return (
    <section data-testid="merch-coming-soon" className="relative min-h-[80vh] flex items-center justify-center overflow-hidden border-b border-[#222]">
      <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url('${bg}')` }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/70" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="inline-flex items-center gap-2 bg-[#E60000] text-white px-4 py-2 mb-8 text-[10px] uppercase tracking-[0.35em] font-black border-2 border-black">
          <Flame className="w-4 h-4" strokeWidth={2.5} />
          <span>{settings.eyebrow || "Squad Shop — Dropping Soon"}</span>
        </div>
        <h1 data-testid="merch-coming-soon-title" className="font-heading text-5xl sm:text-7xl md:text-8xl text-white leading-[0.95] mb-6">
          {settings.title_line1 || "Coming"} <span className="text-[#E60000]">{settings.title_accent || "Soon"}</span>
        </h1>
        <p className="text-base sm:text-lg text-white/80 max-w-xl mx-auto mb-3 leading-relaxed">
          {settings.subtitle || "We're stitching, screen-printing and stacking the official Hillboyz drop."}
        </p>
        {settings.tagline && (
          <p className="text-sm text-white/60 max-w-xl mx-auto mb-10 uppercase tracking-widest">{settings.tagline}</p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
          <Link data-testid="merch-back-home" to="/" className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-[#333] hover:border-[#E60000] font-bold uppercase tracking-widest text-xs px-6 h-12 transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> Back to Home
          </Link>
          {settings.notify_url && (
            <a data-testid="merch-notify-cta" href={settings.notify_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-xs px-6 h-12 transition-colors border-2 border-transparent hover:border-white">
              <Mail className="w-4 h-4" strokeWidth={2.5} /> {settings.notify_label || "Notify Me"}
            </a>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
          {preview.map((label, i) => (
            <div key={i} className="border-2 border-[#222] bg-black/40 backdrop-blur-sm p-4 hover:border-[#E60000] transition-colors">
              <ShoppingBag className="w-5 h-5 text-[#E60000] mx-auto mb-2" strokeWidth={2.5} />
              <div className="text-[11px] uppercase tracking-widest text-white/80 font-bold">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ShopGrid = ({ settings, products }) => (
  <section data-testid="merch-shop" className="bg-[#0A0A0A] py-20 md:py-28 border-b border-[#222]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-3">{settings.eyebrow || "Squad Shop"}</div>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl text-white">
            Rep <span className="text-[#E60000]">The Squad</span>
          </h1>
        </div>
        <p className="text-white/60 max-w-md">{settings.subtitle || "Official Hillboyz gear — wear the flame."}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {products.map((p) => (
          <article key={p.id} data-testid={`merch-product-${p.id}`} className="bg-[#1A1A1A] border-2 border-[#222] hover:border-[#E60000] group transition-colors flex flex-col">
            <div className="aspect-square bg-black overflow-hidden relative">
              {p.image_url ? (
                <img src={resolveImageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20">
                  <ShoppingBag className="w-16 h-16" strokeWidth={1.5} />
                </div>
              )}
              {p.badge && (
                <div className="absolute top-3 left-3 bg-[#E60000] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1">{p.badge}</div>
              )}
              {!p.in_stock && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <span className="bg-black border-2 border-[#E60000] text-white text-xs uppercase tracking-widest font-bold px-4 py-2">Sold Out</span>
                </div>
              )}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <h3 className="font-heading text-2xl text-white leading-tight mb-1">{p.name}</h3>
              <div className="text-[#E60000] font-bold text-lg mb-2">${Number(p.price).toFixed(2)}</div>
              {p.description && <p className="text-white/60 text-sm leading-relaxed mb-4 flex-1">{p.description}</p>}
              {p.buy_url && p.in_stock ? (
                <a href={p.buy_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-xs px-4 h-11 transition-colors border-2 border-transparent hover:border-white">
                  Buy Now <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
                </a>
              ) : (
                <button disabled className="inline-flex items-center justify-center gap-2 bg-[#1A1A1A] border-2 border-[#333] text-white/40 font-bold uppercase tracking-widest text-xs px-4 h-11 cursor-not-allowed">
                  {p.in_stock ? "Coming Soon" : "Sold Out"}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default Merch;
